import {
  Breadcrumb,
  Button,
  message,
  Select,
  Table,
  Tooltip,
  Typography,
} from "antd";
import classes from "./AppDashboard.module.scss";
import { queryKeys } from "../../constants";
import Loader from "../../components/Loading";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  fetchFreshBooksDetails,
  getFreshBooksBusiness,
} from "../../services/freshbook";
import { FundViewOutlined } from "@ant-design/icons";
import { getThemeColor } from "../../helpers";
import {
  clientsColumns,
  expensesCategoriesColumns,
  expensesColumns,
  invoiceColumns,
  itemColumns,
  options,
  paymentColumns,
  staffColumns,
  taskColumns,
  vendorColumns,
} from "./constants";

const { Title, Text } = Typography;

const FreshBookDashboard = () => {
  const businessColumns = [
    {
      title: "Business Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Account Id",
      dataIndex: "account_id",
      key: "account_id",
    },
    {
      title: "Date Format",
      dataIndex: "date_format",
      key: "date_format",
    },
    {
      title: "First Day Of Week",
      dataIndex: "first_day_of_week",
      key: "first_day_of_week",
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (text, record) => <span>{text.toString()}</span>,
    },
    {
      title: "Timezone",
      dataIndex: "timezone",
      key: "timezone",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Industry",
      dataIndex: "industry",
      key: "industry",
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      render: (text, record) => (
        <span>{record?.phone_number?.phone_number || ""}</span>
      ),
    },
    {
      title: "Actions",
      key: "action",
      render: (record) => (
        <Tooltip title="View Details">
          <FundViewOutlined
            style={{ color: getThemeColor(), cursor: "pointer" }}
            onClick={() => handleSelect(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState(businessColumns);
  const [dataSource, setDataSource] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("bill_payments");

  const getFreshBooksBusinessQuery = useQuery({
    queryKey: [queryKeys.freshBooksBusiness],
    queryFn: () => getFreshBooksBusiness(),
    onError: (error) => {
      message.error(`Error: ${error.message}`);
    },
  });

  const getFreshBookQuery = useMutation({
    mutationFn: fetchFreshBooksDetails,
    onSuccess: () => {
      message.success("Details fetched successfully");
      setIsLoading(false);
    },
    onError: (error) => {
      message.error(`Error: Something went wrong`);
      setIsLoading(false);
    },
  });

  const setDataBasedOnCategory = (data, category) => {
    switch (category) {
      case "bill_payments":
        setDataSource(data?.bill_payments || []);
        setColumns(paymentColumns);
        break;
      case "tasks":
        setDataSource(data?.tasks || []);
        setColumns(taskColumns);
        break;
      case "invoices":
        setDataSource(data?.invoices || []);
        setColumns(invoiceColumns);
        break;
      case "items":
        setDataSource(data?.items || []);
        setColumns(itemColumns);
        break;
      case "bill_vendors":
        setDataSource(data?.bill_vendors || []);
        setColumns(vendorColumns);
        break;
      case "clients":
        setDataSource(data?.clients || []);
        setColumns(clientsColumns);
        break;
      case "expenses":
        setDataSource(data?.expenses || []);
        setColumns(expensesColumns);
        break;
      case "expenses_categories":
        setDataSource(data?.categories || []);
        setColumns(expensesCategoriesColumns);
        break;
      case "staff":
        setDataSource(data?.staff || []);
        setColumns(staffColumns);
        break;
      default:
        setColumns([]);
        setDataSource([]);
    }
  };

  const handleSelect = (record) => {
    setIsLoading(true);
    getFreshBookQuery
      .mutateAsync({
        account_id: record.account_id,
        object_type: selectedCategory,
      })
      .then((res) => {
        setSelectedBusiness(record);
        setDataBasedOnCategory(res?.data, selectedCategory);
        setBreadcrumbItems([
          {
            title: (
              <span className={classes.workspaceBreadcrumb}>Business List</span>
            ),
            onClick: () => {
              setSelectedBusiness(null);
              setColumns(businessColumns);
              setDataSource(getFreshBooksBusinessQuery?.data?.data);
            },
          },
          {
            title: record.name,
          },
        ]);
        setIsLoading(false);
      });
  };

  const handleChange = (value) => {
    setSelectedCategory(value);
    if (selectedBusiness) {
      setIsLoading(true);
      getFreshBookQuery
        .mutateAsync({
          account_id: selectedBusiness.account_id,
          object_type: value,
        })
        .then((res) => {
          setDataBasedOnCategory(res?.data, value);
        });
    }
  };

  useEffect(() => {
    if (getFreshBooksBusinessQuery?.data?.data) {
      setDataSource(getFreshBooksBusinessQuery?.data?.data);
    }
  }, [getFreshBooksBusinessQuery?.data?.data]);

  return getFreshBooksBusinessQuery?.isLoading ? (
    <Loader />
  ) : (
    <>
      <div className={classes.header}>
        <Title level={4} style={{ marginTop: 0 }}>
          FreshBook App Dashboard
        </Title>

        <div className={classes.headingContainer}>
          <span className={classes.heading}>Business List</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Lorem Ipsum is simply dummy text of the printing and typesetting
            industry. Lorem Ipsum has been the industry's standard dummy text
            ever since the 1500s. Lorem Ipsum is simply dummy text of the
            printing and typesetting industry. Lorem Ipsum has been the
            industry's standard dummy text ever since the 1500s.
          </Text>
        </div>

        <div>
          <Select
            onChange={handleChange}
            value={selectedCategory}
            title="Select Category"
            placeholder="Select Category and then load data for it by clicking on any business details icon"
            options={options}
            style={{ width: "100%", marginBottom: 20, marginTop: 20 }}
            showSearch
          />
        </div>

        {selectedBusiness && (
          <div className={classes.breadcrumb}>
            <Breadcrumb items={breadcrumbItems} separator=">" />
          </div>
        )}

        <Table
          columns={columns || []}
          dataSource={dataSource || []}
          rowKey="id"
          size="small"
          scroll={{ x: "auto" }} // Enable horizontal and vertical scrolling
          loading={isLoading}
        />
      </div>
    </>
  );
};

export default FreshBookDashboard;
